import { defineStore } from 'pinia'

import type { Placement } from '@/types/placement'
import type { PublishedVariant } from '@/types/publishedVariant'
import type { ProductData } from '@/helpers/productPlacementHelper'
import type { FabricImage } from 'fabric'
import type { AspectCounts, PlacementSpace } from '@/constants/placementStrategies'
import { getDefaultPlacementArea } from '@/helpers/placementHelper'
import type { PlacementResult } from '@/types/placementResult'

interface PlacementState {
  placements: Placement[]
  selectedPlacement: Placement | null
  backgroundImages: string[]
  aiBackgroundImages: string[]
  selectedBackgroundImage: string
  selectedAIBackgroundImage: string
  baseBackgroundImage: string
  baseAIBackgroundImage: string
  selectedPublishedVariant: PublishedVariant | undefined
  selectedContactShadowEllipseIndex: number | 0
  selectedContactShadowSquareIndex: number | 0
  placementOptions: string[]
  selectedPlacementOption: string
  defaultPlacement: PlacementResult | null
  processedProductData: { productData: ProductData; fabricImage: FabricImage }[]
  basePlacementType: string
  selectedRotateShadowIndex: number | 0
  aspectCounts: AspectCounts | null
  currentPlacementArea: PlacementSpace
  basePlacementArea: PlacementSpace
  selectedOverlayImage: string
}

export const usePlacementStore = defineStore('placement', {
  state: (): PlacementState => ({
    placements: [],
    selectedPlacement: null,
    backgroundImages: [],
    aiBackgroundImages: [],
    baseBackgroundImage: '',
    baseAIBackgroundImage: '',
    selectedBackgroundImage: '',
    selectedAIBackgroundImage: '',
    selectedPublishedVariant: undefined,
    selectedContactShadowEllipseIndex: 0,
    selectedContactShadowSquareIndex: 0,
    placementOptions: [],
    selectedPlacementOption: '',
    defaultPlacement: null,
    processedProductData: [],
    basePlacementType: '',
    selectedRotateShadowIndex: 0,
    aspectCounts: null,
    currentPlacementArea: getDefaultPlacementArea(),
    basePlacementArea: getDefaultPlacementArea(),
    selectedOverlayImage: '',
  }),
  actions: {
    setPlacements(placements: Placement[]): void {
      this.placements = placements
    },
    setPlacement(placement: Placement | null): void {
      this.selectedPlacement = placement
      this.backgroundImages = JSON.parse(JSON.stringify(placement?.backgroundImages)) ?? []
      this.selectedBackgroundImage = placement?.backgroundImages[0] ?? ''
      this.selectedPublishedVariant = placement?.publishedVariants[0]
    },
    setPublishedVariant(publishedVariant: PublishedVariant): void {
      this.selectedPublishedVariant = publishedVariant
    },
    setCanvasJson(json: any): void {
      if (this.selectedPublishedVariant) {
        this.selectedPublishedVariant.canvasJson = json
      }
    },
    AddBackgroundImage(texture: string): void {
      this.backgroundImages.push(texture)
      this.selectedBackgroundImage = texture
    },
    addAiBackgroundImage(image: string): void {
      this.aiBackgroundImages.push(image)
    },
    setAIBackgroundImages(images: string[]): void {
      this.aiBackgroundImages = images
    },
    setSelectedBackgroundImage(texture: string): void {
      this.selectedBackgroundImage = texture
    },
    setSelectedAIBackgroundImage(texture: string): void {
      this.selectedAIBackgroundImage = texture
    },
    setBaseBackgroundImage(texture: string): void {
      this.baseBackgroundImage = texture
      this.selectedBackgroundImage = texture
    },
    setBaseAIBackgroundImage(texture: string): void {
      this.baseAIBackgroundImage = texture
      this.selectedAIBackgroundImage = texture
    },
    resetPlacementState(): void {
      this.backgroundImages = []
      this.selectedPlacement = null
      this.placementOptions = []
      this.selectedPlacementOption = ''
      this.defaultPlacement = null
      this.processedProductData = []
    },
    setContactShadowEllipseIndex(index: number): void {
      this.selectedContactShadowEllipseIndex = index
    },
    setContactShadowSquareIndex(index: number): void {
      this.selectedContactShadowSquareIndex = index
    },
    resetSelectedPlacementOption(basePlacementType: string): void {
      this.selectedPlacementOption = basePlacementType
    },
    setProcessedProductData(
      processedProductData: { productData: ProductData; fabricImage: FabricImage }[],
    ): void {
      this.processedProductData = processedProductData
    },
    setSelectedRotateIndex() {
      this.selectedRotateShadowIndex = Math.floor(Math.random() * 4)
    },
    setOverlayImage(image: string): void {
      this.selectedOverlayImage = image
    },
  },
})
